/**
 * @module app.core
 */

import { autoinject, computedFrom } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import * as CD from 'app/cd/models/essette-api';
import { IPopup } from 'app/shared/elements/popup-base';
import { apiCall } from 'app/shared/lib/essette-api';
import { IMemberSearchFilters, MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { EditAddress } from 'app/core/components/addresses/edit-address';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';


@autoinject()
export class ManageMemberAddress extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected memberId: string | null = null;

    protected memberAddresses: Array<CD.MemberAddress> = [];
    protected memberSearchFilters: IMemberSearchFilters = {
        alternateTypes: false
    };

    protected editedAddress: CD.MemberAddress | null = null;
    protected editAddressIndex: number | null = null;
    protected types: Core.AddressType[] = [];
    protected allTypes: Core.AddressType[] = [];
    protected validate = validate;
    protected isValid: boolean = false;

    ////****************** Element References ***************************************////
    private $saveAddressModal!: IPopup;
    protected $saveAddressForm!: IForm;
    protected $saveAddress!: EditAddress;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAllTypes();
        await this.loadMemberAddresses();
        this.updateAvailableTypes();
    }

    protected closeSaveAddressModal(): void {
        this.$saveAddress.address = {};
        this.$saveAddressModal.close();
    }

    protected openAddressModal(addressIndex?: number): void {
        if(addressIndex != null) {
            this.editAddressIndex = addressIndex;
            this.$saveAddress.address = deepClone(this.memberAddresses[addressIndex]);
        } else {
            this.editAddressIndex = null;
        }
        if(this.$saveAddressModal) {
            this.$saveAddressModal.open();
        }
    }

    protected addAddress(): void {
        if(!this.memberAddresses) this.memberAddresses = []; // sets addresses in the instance where it was explicitly set to null or undefined
        if(this.isValid) {
            const typeExists = this.memberAddresses.some(address => address.type?.id === this.$saveAddress.address.type?.id);
            if(typeExists) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.add_error', { what: this.i18n.tr('core:address.type.$'), item: this.i18n.tr('core:address.$') })).show();
            }
            else {
                const address = this.$saveAddress.address;
                this.saveAddress(address);
                this.closeSaveAddressModal();
            }
        }
    }

    protected editAddress(addressIndex: number): void {
        const typeExists = this.memberAddresses.some(address => address.type?.id === this.$saveAddress.address.type?.id);
        // Check if form is valid and either the address is the same as the one being edited or a newly added address
        if(this.isValid && (this.memberAddresses[addressIndex].type?.id === this.$saveAddress.address.type?.id || !typeExists)) {
            this.saveAddress(this.$saveAddress.address);
            this.closeSaveAddressModal();
        } else {
            this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.edit_error', { what: this.i18n.tr('core:address.type.$'), item: this.i18n.tr('core:address.$') })).show();
        }
    }

    @computedFrom('types')
    protected get isAddButtonDisabled(): boolean {
        return this.types.length === 0;
    }

    private updateAvailableTypes(): void {
        this.types = this.allTypes.filter(type => !this.memberAddresses.some(address => address.type?.id === type.id));
    }

    protected buildAddress(address: CD.MemberAddress): CD.MemberAddress {
        return {
            id: forceNull(address.id),
            type: {
                code: address.type?.code
            },
            member: { id: this.memberId },
            street1: address.street1,
            street2: forceNull(address.street2),
            city: address.city,
            region: {
                id: address.region?.id
            },
            postalCode: address.postalCode,
            area: address.area
        };
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAllTypes(): Promise<void> {
        try {
            this.state.beginLoad();

            const types = await this.memberService.loadMemberAddressTypes(undefined, undefined, this.memberSearchFilters);
            this.allTypes = types.results;
        } catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberAddresses(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true
        };

        const includes: string[] = [
            'region',
            'type'
        ];

        const searchFilters: IMemberSearchFilters = {
            alternateTypes: false
        };

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberAddresses(this.memberId, undefined, options, includes, undefined, searchFilters);
            this.memberAddresses = response.results;
        } catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveAddress(address: CD.MemberAddress): Promise<void> {
        if(!address || !this.memberId) {
            return;
        }
        let isSuccessful = false;
        const saveAddress = this.buildAddress(address);

        try {
            this.state.beginSave();
            const response = await this.memberService.saveMemberAddress(saveAddress);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            isSuccessful = false;
            throw error;
        }
        finally {
            this.toaster.standard.updated('cd:member.address.$', isSuccessful);
            if(isSuccessful) {
                this.reload();
            }
            this.state.endSave();
        }
    }

    @apiCall
    private async activateAddress(address: CD.MemberAddress, isActive: boolean): Promise<void> {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cd:member.address.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberAddress(address.id!, isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:member.address.$', isActive, isSuccessful);
            if(isSuccessful) {
                this.reload();
            }
            this.state.endSave();
        }
    }
}
